import { trackLoss, trackStep } from "./funnel";

/**
 * Funnel events for the card / invitation / menu editor (the design page).
 *
 * Invitations have never sold, and nothing recorded where people dropped off:
 * whether they opened the editor, picked a design, or tried to buy and were
 * refused. Each event goes to the two places the shop already reports to —
 *
 *   - Sentry, through funnel.ts: a breadcrumb for each step (free, rides along
 *     on any later error) and a real event for a failed add-to-cart, which is
 *     a customer turned away at the last step;
 *   - Google Analytics (and the Meta pixel for the add-to-cart itself) when
 *     the page has them. They are only loaded on production hosts
 *     (app/layout.tsx `trackingEnabled`), so stage and local send nothing.
 *
 * No new vendor. Every call swallows its own errors — instrumentation must
 * never be why a customer cannot design or buy.
 */

type Gtag = (...args: unknown[]) => void;
type AnalyticsWindow = Window & { gtag?: Gtag; fbq?: (...args: unknown[]) => void };

/** gtag/fbq are loaded lazily; wait a little for them rather than drop the event. */
function whenReady(test: (w: AnalyticsWindow) => boolean, fn: (w: AnalyticsWindow) => void): void {
  if (typeof window === "undefined") return;
  const w = window as AnalyticsWindow;
  let tries = 0;
  const attempt = () => {
    try {
      if (test(w)) {
        fn(w);
        return;
      }
    } catch {
      return;
    }
    if (++tries < 20) setTimeout(attempt, 500);
  };
  attempt();
}

/** A GA4 event once gtag is there (production hosts only); never throws. */
export function gaEvent(name: string, params: Record<string, unknown>): void {
  whenReady(
    (w) => typeof w.gtag === "function",
    (w) => w.gtag!("event", name, params),
  );
}

/** Short, low-cardinality string for analytics parameters. */
const clip = (v: string | null | undefined, n = 100) => (v ?? "").slice(0, n);

export interface EditorContext {
  /** Product kind: "invitation", "greeting-card", "menu", … */
  kind: string;
  definitionId: string;
}

/** The design page finished loading its product and showed the editor. */
export function trackEditorOpened(
  ctx: EditorContext & { filter: string | null; templateParam: string | null; locale: string },
): void {
  const detail = {
    kind: ctx.kind,
    definitionId: ctx.definitionId,
    filter: ctx.filter ?? "",
    template: ctx.templateParam ?? "",
    locale: ctx.locale,
  };
  trackStep("editor-opened", detail);
  gaEvent("editor_open", {
    product_kind: ctx.kind,
    definition_id: ctx.definitionId,
    template_filter: detail.filter,
    template_link: clip(detail.template),
  });
}

/** A template was applied — from the picker or from a ?template= deep link. */
export function trackTemplateChosen(
  ctx: EditorContext & { code: string; source: "picker" | "link" },
): void {
  trackStep("template-chosen", { ...ctx });
  gaEvent("template_select", {
    product_kind: ctx.kind,
    definition_id: ctx.definitionId,
    template_code: clip(ctx.code),
    source: ctx.source,
  });
}

/** The design is in the cart. `value` in minor units of `currency`. */
export function trackCardAddedToCart(
  ctx: EditorContext & { quantity: number; value: number; currency: string; templateCode?: string | null },
): void {
  // ISO code for the analytics tools ("eur-at" is a market label, not ISO).
  const iso = (ctx.currency || "eur").split("-")[0].toUpperCase();
  const value = Math.round(ctx.value) / 100;
  trackStep("card-added-to-cart", { ...ctx });
  gaEvent("add_to_cart", {
    currency: iso,
    value,
    items: [
      {
        item_id: ctx.definitionId,
        item_category: ctx.kind,
        item_variant: clip(ctx.templateCode ?? ""),
        quantity: ctx.quantity,
      },
    ],
  });
  whenReady(
    (w) => typeof w.fbq === "function",
    (w) =>
      w.fbq!("track", "AddToCart", {
        content_ids: [ctx.definitionId],
        content_type: "product",
        content_category: ctx.kind,
        value,
        currency: iso,
      }),
  );
}

/** Adding the design to the cart failed; `message` is what the customer saw. */
export function trackCardAddToCartFailed(
  ctx: EditorContext & { message: string; quantity: number },
): void {
  trackLoss("card-add-to-cart-failed", {
    kind: ctx.kind,
    definitionId: ctx.definitionId,
    quantity: ctx.quantity,
    message: clip(ctx.message, 500),
  });
  gaEvent("add_to_cart_failed", {
    product_kind: ctx.kind,
    definition_id: ctx.definitionId,
    error_message: clip(ctx.message),
  });
}
